import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  BarChart3,
  Mail,
  Eye,
  Percent,
  Search,
  CheckCircle2,
  Clock,
  User,
  Target,
  Users,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useApiClient } from "@/hooks/use-api-client";

interface LogRecord {
  id: string;
  bulletin_id: string;
  recipient_email: string;
  recipient_name: string;
  recipient_type: string;
  status: "sent" | "opened" | "bounced";
  sent_at: string;
  opened_at?: string;
  open_count: number;
}

interface AnalyticsData {
  total_sent: number;
  total_opened: number;
  open_rate_pct: number;
  logs: LogRecord[];
}

interface BulletinAnalyticsModalProps {
  bulletinId: string | null;
  bulletinTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

export function BulletinAnalyticsModal({
  bulletinId,
  bulletinTitle,
  isOpen,
  onClose,
}: BulletinAnalyticsModalProps) {
  const apiClient = useApiClient();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "opened" | "pending">("all");

  const fetchAnalytics = async () => {
    if (!bulletinId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/v1/bulletins/${bulletinId}/analytics`);
      const analyticsData = res.ok ? res.data?.analytics : res.analytics;
      if (analyticsData) {
        setData(analyticsData);
      }
    } catch (err: any) {
      console.error("Error loading analytics:", err);
      toast.error("No se pudieron cargar las métricas de lectura.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && bulletinId) {
      fetchAnalytics();
    }
  }, [isOpen, bulletinId]);

  if (!isOpen) return null;

  const logs = data?.logs || [];

  const filteredLogs = logs.filter((l) => {
    const matchesSearch =
      l.recipient_email.toLowerCase().includes(search.toLowerCase()) ||
      (l.recipient_name && l.recipient_name.toLowerCase().includes(search.toLowerCase()));

    if (statusFilter === "opened") {
      return matchesSearch && (l.status === "opened" || !!l.opened_at);
    }
    if (statusFilter === "pending") {
      return matchesSearch && l.status !== "opened" && !l.opened_at;
    }
    return matchesSearch;
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl rounded-2xl p-0 overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-2xl">
        <DialogHeader className="p-6 pb-4 bg-slate-50 dark:bg-slate-800/40 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400">
              <BarChart3 className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-slate-900 dark:text-white line-clamp-1">
                Métricas de Lectura y Apertura
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 mt-0.5">
                {bulletinTitle}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 space-y-6">
          {/* Analytics Overview Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Sent Card */}
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-800">
              <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-1">
                <span className="text-xs font-semibold uppercase tracking-wider">Total Enviados</span>
                <Mail className="h-4 w-4 text-blue-500" />
              </div>
              <p className="text-2xl font-extrabold text-slate-900 dark:text-white">
                {loading ? "..." : data?.total_sent || 0}
              </p>
              <span className="text-[11px] text-slate-400">Correos despachados</span>
            </div>

            {/* Opened Card */}
            <div className="p-4 rounded-2xl bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-800/50">
              <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400 mb-1">
                <span className="text-xs font-semibold uppercase tracking-wider">Total Leídos / Abiertos</span>
                <Eye className="h-4 w-4" />
              </div>
              <p className="text-2xl font-extrabold text-emerald-700 dark:text-emerald-300">
                {loading ? "..." : data?.total_opened || 0}
              </p>
              <span className="text-[11px] text-emerald-600/80 dark:text-emerald-400/80 font-medium">
                Lecturas confirmadas por píxel
              </span>
            </div>

            {/* Open Rate Card */}
            <div className="p-4 rounded-2xl bg-purple-50/60 dark:bg-purple-950/20 border border-purple-200/60 dark:border-purple-800/50">
              <div className="flex items-center justify-between text-purple-600 dark:text-purple-400 mb-1">
                <span className="text-xs font-semibold uppercase tracking-wider">Tasa de Apertura</span>
                <Percent className="h-4 w-4" />
              </div>
              <p className="text-2xl font-extrabold text-purple-700 dark:text-purple-300">
                {loading ? "..." : `${data?.open_rate_pct || 0}%`}
              </p>

              {/* Progress Bar */}
              <div className="w-full bg-purple-200 dark:bg-purple-900/50 h-1.5 rounded-full overflow-hidden mt-2">
                <div
                  className="bg-purple-600 dark:bg-purple-400 h-full rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(data?.open_rate_pct || 0, 100)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar destinatario..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 rounded-xl text-xs bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700"
              />
            </div>

            <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-full sm:w-auto">
              <Button
                variant={statusFilter === "all" ? "default" : "ghost"}
                size="sm"
                onClick={() => setStatusFilter("all")}
                className="rounded-lg text-xs font-bold px-3 h-7"
              >
                Todos ({logs.length})
              </Button>
              <Button
                variant={statusFilter === "opened" ? "default" : "ghost"}
                size="sm"
                onClick={() => setStatusFilter("opened")}
                className="rounded-lg text-xs font-bold px-3 h-7 text-emerald-600 dark:text-emerald-400"
              >
                🟢 Abiertos ({data?.total_opened || 0})
              </Button>
              <Button
                variant={statusFilter === "pending" ? "default" : "ghost"}
                size="sm"
                onClick={() => setStatusFilter("pending")}
                className="rounded-lg text-xs font-bold px-3 h-7 text-slate-500"
              >
                ⚪ Pendientes ({(data?.total_sent || 0) - (data?.total_opened || 0)})
              </Button>
            </div>
          </div>

          {/* Recipients Table */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-2" />
              <span className="text-xs font-medium">Cargando registros de lecturas...</span>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-10 bg-slate-50 dark:bg-slate-800/20 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
              <Mail className="h-8 w-8 mx-auto text-slate-300 dark:text-slate-600 mb-2" />
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">
                No hay destinatarios registrados para esta vista.
              </p>
            </div>
          ) : (
            <div className="max-h-[300px] overflow-y-auto custom-scrollbar border border-slate-200 dark:border-slate-800 rounded-xl divide-y divide-slate-100 dark:divide-slate-800">
              {filteredLogs.map((log) => {
                const isOpened = log.status === "opened" || !!log.opened_at;
                return (
                  <div
                    key={log.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 px-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors gap-2"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-xs ${
                          isOpened
                            ? "bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-500"
                        }`}
                      >
                        {isOpened ? <Eye className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                            {log.recipient_name || "Usuario"}
                          </span>
                          <Badge
                            variant="outline"
                            className={`text-[9px] font-semibold px-1.5 py-0 rounded-md ${
                              log.recipient_type === "marketing_lead"
                                ? "bg-purple-50 text-purple-600 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800"
                                : "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800"
                            }`}
                          >
                            {log.recipient_type === "marketing_lead" ? "🎯 Prospecto Marketing" : "👥 Usuario Plataforma"}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                          {log.recipient_email}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-4 text-xs">
                      {isOpened ? (
                        <div className="text-right">
                          <Badge className="bg-emerald-500 text-white font-bold text-[10px] px-2 py-0.5 rounded-full mb-0.5">
                            🟢 Abierto ({log.open_count || 1} {log.open_count === 1 ? "vez" : "veces"})
                          </Badge>
                          {log.opened_at && (
                            <p className="text-[10px] text-slate-400 flex items-center justify-end gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(log.opened_at).toLocaleString("es-CO", {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                          )}
                        </div>
                      ) : (
                        <Badge variant="secondary" className="bg-slate-100 dark:bg-slate-800 text-slate-500 text-[10px] px-2 py-0.5 rounded-full">
                          ⚪ Enviado (Pendiente)
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="p-4 px-6 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
          <span className="text-[11px] text-slate-400">
            Las lecturas se actualizan automáticamente en tiempo real mediante el píxel de seguimiento.
          </span>
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-xl px-5 text-xs font-bold border-slate-200 dark:border-slate-700"
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
